import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import bigCommerceLogo from '@/assets/plugins/Logo_bigcommerce.svg';
import falabellaLogo from '@/assets/plugins/Logo_falabella.svg';
import jumpsellerLogo from '@/assets/plugins/Logo_jumpseller.svg';
import magentoLogo from '@/assets/plugins/Logo_magento.svg';
import mercadoEnviosLogo from '@/assets/plugins/Logo_mercadoEnvios.svg';
import mlEnvios99Logo from '@/assets/plugins/Logo_mercadoEnvios+envios99.png';
import mlFulfillLogo from '@/assets/plugins/Logo_mercadoEnvios+fulfill99.png';
import shopifyLogo from '@/assets/plugins/Logo_shopify.svg';
import tiendaNubeLogo from '@/assets/plugins/Logo_tiendanube.svg';
import tikTokShopLogo from '@/assets/plugins/Logo_tiktokshop.svg';
import woocommerceLogo from '@/assets/plugins/Logo_woocommerce.svg';
import { COUNTRY_MEX } from '@/constants/countries';
import { isFalabellaEnabledCountry } from '@/constants/falabella';
import { useFulfillAccess } from '@/hooks/useFulfillAccess';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import {
  setEnvioFlexAccess,
  setFalabellaAccess,
  setMLFulfillAccess,
  setMLParcelAccess,
} from '@/redux/parcel/parcelSlice';
import { AppDispatch, RootState } from '@/redux/store';
import { IResUserPlugins } from '@/types/plugins/envioFlex';

import type { CSSProperties } from 'react';

type PluginStatus = 'connected' | 'disconnected' | 'error' | 'disabled';

interface Plugin {
  id: string;
  name: string;
  logo: string;
  logoStyle?: CSSProperties;
  logoSuffix?: string;
  status: PluginStatus;
}

const allPlugins: Plugin[] = [
  {
    id: 'mercado-envios',
    name: 'Mercado Libre Flex',
    logo: mercadoEnviosLogo,
    logoSuffix: 'FLEX',
    status: 'disconnected',
  },
  {
    id: 'mercado-envios-fulfill99',
    name: 'Mercado Libre Fulfill99',
    logo: mlFulfillLogo,
    logoStyle: { height: '96px', maxWidth: '100%' },
    status: 'disconnected',
  },
  {
    id: 'mercado-parcel',
    name: 'Mercado Libre Parcel',
    logo: mlEnvios99Logo,
    logoStyle: { height: '96px', maxWidth: '100%' },
    status: 'disconnected',
  },
  {
    id: 'falabella',
    name: 'Falabella',
    logo: falabellaLogo,
    logoStyle: { height: '24px' },
    status: 'disabled',
  },
  {
    id: 'woocommerce',
    name: 'WooCommerce',
    logo: woocommerceLogo,
    status: 'disabled',
  },
  {
    id: 'shopify',
    name: 'Shopify',
    logo: shopifyLogo,
    status: 'disabled',
  },
  {
    id: 'tiendanube',
    name: 'Tiendanube',
    logo: tiendaNubeLogo,
    status: 'disabled',
  },
  {
    id: 'jumpseller',
    name: 'Jumpseller',
    logo: jumpsellerLogo,
    status: 'disabled',
  },
];

interface DocumentationPlugin {
  id: string;
  name: string;
  logo: string;
  logoStyle?: CSSProperties;
  documentation_url: string;
}

// Plataformas que el cliente instala por su cuenta siguiendo la guía de developers.99minutos.com
const documentationPlugins: DocumentationPlugin[] = [
  {
    id: 'shopify',
    name: 'Shopify',
    logo: shopifyLogo,
    documentation_url: 'https://developers.99minutos.com/page/shopify',
  },
  {
    id: 'woocommerce',
    name: 'WooCommerce',
    logo: woocommerceLogo,
    documentation_url: 'https://developers.99minutos.com/page/plugin-installation-woocommerce',
  },
  {
    id: 'tiktok-shop',
    name: 'TikTok Shop',
    logo: tikTokShopLogo,
    documentation_url: 'https://developers.99minutos.com/page/tiktok-shop',
  },
  {
    id: 'magento',
    name: 'Magento',
    logo: magentoLogo,
    documentation_url: 'https://developers.99minutos.com/page/plugin-installation-magento',
  },
  {
    id: 'jumpseller',
    name: 'Jumpseller',
    logo: jumpsellerLogo,
    documentation_url: 'https://developers.99minutos.com/page/plugin-installation-jumpseller',
  },
  {
    id: 'tiendanube',
    name: 'Tiendanube',
    logo: tiendaNubeLogo,
    documentation_url: 'https://developers.99minutos.com/page/plugin-installation-tienda-nube',
  },
  {
    id: 'bigcommerce',
    name: 'BigCommerce',
    logo: bigCommerceLogo,
    documentation_url: 'https://developers.99minutos.com/page/api-installation-bigcommerce',
  },
];

const useMyPlugins = () => {
  const { data: profile } = useSelector((state: RootState) => state.parcel.profile);
  const { hasFulfillAccess } = useFulfillAccess();
  const dispatch = useDispatch<AppDispatch>();
  const [plugins, setPlugins] = useState<Plugin[]>(allPlugins);
  const [activeConnector, setActiveConnector] = useState<string>('');
  const [activeConfiguration, setActiveConfiguration] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [mercadoPluginId, setMercadoPluginId] = useState<string>('');
  const [mlFulfillPluginId, setMlFulfillPluginId] = useState<string>('');
  const [mlParcelPluginId, setMlParcelPluginId] = useState<string>('');
  const [falabellaPluginId, setFalabellaPluginId] = useState<string>('');
  const [webhookPlatformOwner, setWebhookPlatformOwner] = useState<string | null>(null);
  const [activeMlCount, setActiveMlCount] = useState<number>(0);

  // Fulfill99 solo se muestra con acceso a Fulfill. Falabella se oculta en México porque
  // no llegará a ese país; en los demás países no habilitados aparece como "Próximamente".
  const isMexicoAccount = profile?.country === COUNTRY_MEX;
  const isFalabellaEnabled = isFalabellaEnabledCountry(profile?.country);
  const visiblePlugins: Plugin[] = [];
  for (const plugin of plugins) {
    const isHiddenFulfill = plugin.id === 'mercado-envios-fulfill99' && !hasFulfillAccess;
    const isHiddenFalabella = plugin.id === 'falabella' && isMexicoAccount;
    if (isHiddenFulfill || isHiddenFalabella) {
      continue;
    }
    if (plugin.id === 'falabella' && !isFalabellaEnabled) {
      visiblePlugins.push({ ...plugin, status: 'disabled' });
      continue;
    }
    visiblePlugins.push(plugin);
  }

  const configurablePlugins = visiblePlugins.filter((plugin) => plugin.status !== 'disabled');
  const upcomingPlugins = visiblePlugins.filter((plugin) => plugin.status === 'disabled');

  const handleActiveConnector = (name: string) => {
    if (name.toLowerCase() === 'falabella' && profile?.country !== 'CHL') {
      toast.error('Falabella actualmente solo está disponible para Chile (CHL)');
      return;
    }
    setActiveConnector(name);
  };
  const handleActiveConfiguration = (name: string) => {
    setActiveConfiguration(name);
  };

  const updatePluginStatus = (id: string, status: PluginStatus) => {
    setPlugins((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
  };

  const handleConnect = async (id: string) => {
    const clientId = profile?.credentials.clientId;
    const country = profile?.country;

    if (!clientId || !country) {
      console.error('ClientId or country not available');
      updatePluginStatus(id, 'error');
      return;
    }

    try {
      const http = initializeHttpProvider();
      if (id === 'mercado-envios-fulfill99') {
        await http.mlFulfill.install({ country, clientId });
      } else if (id === 'mercado-parcel') {
        await http.mlParcel.install({ country, clientId });
      } else if (id === 'falabella') {
        if (!isFalabellaEnabledCountry(country)) {
          toast.error('Falabella actualmente solo está disponible para Chile (CHL)');
          return;
        }
        // Falabella abre su modal connector propio para pedir credenciales,
        // esto es manejado en MyPlugins.tsx por el handleActiveConnector
        handleActiveConnector('Falabella');
      } else {
        await http.envioFlex.install({ country, clientId });
      }
    } catch (error) {
      console.error('Error connecting plugin:', error);
      updatePluginStatus(id, 'error');
    }
  };

  const handleDisconnect = async (id: string) => {
    const clientId = profile?.credentials.clientId;

    if (id === 'mercado-envios' || id === 'falabella') {
      if (
        !clientId ||
        (!mercadoPluginId && id === 'mercado-envios') ||
        (!falabellaPluginId && id === 'falabella')
      ) {
        console.error('ClientId or pluginId not available');
        toast.error('Error: datos de plugin no disponibles');
        return;
      }
      try {
        const http = initializeHttpProvider();
        let response;
        if (id === 'falabella') {
          response = await http.falabella.deletePlugin({ clientId, pluginId: falabellaPluginId });
        } else {
          response = await http.envioFlex.deletePlugin({ clientId, pluginId: mercadoPluginId });
        }
        if (response.success) {
          updatePluginStatus(
            id,
            id === 'falabella' && !isFalabellaEnabledCountry(profile?.country)
              ? 'disabled'
              : 'disconnected',
          );
          if (id === 'falabella') {
            dispatch(setFalabellaAccess(false));
          } else {
            dispatch(setEnvioFlexAccess(false));
          }
          toast.success('Plugin desconectado exitosamente');
          await fetchUserPlugins();
        } else {
          toast.error('Error al desconectar plugin');
        }
      } catch (error) {
        console.error('Error disconnecting plugin:', error);
        toast.error('Error al desconectar plugin');
      }
    } else if (id === 'mercado-envios-fulfill99') {
      if (!clientId || !mlFulfillPluginId) {
        console.error('ClientId or pluginId not available');
        toast.error('Error: datos de plugin no disponibles');
        return;
      }
      try {
        const http = initializeHttpProvider();
        const response = await http.mlFulfill.deletePlugin({
          clientId,
          pluginId: mlFulfillPluginId,
        });
        if (response.success) {
          updatePluginStatus(id, 'disconnected');
          toast.success('Plugin desconectado exitosamente');
          await fetchUserPlugins();
        } else {
          toast.error('Error al desconectar plugin');
        }
      } catch (error) {
        console.error('Error disconnecting plugin:', error);
        toast.error('Error al desconectar plugin');
      }
    } else if (id === 'mercado-parcel') {
      if (!clientId || !mlParcelPluginId) {
        console.error('ClientId or pluginId not available');
        toast.error('Error: datos de plugin no disponibles');
        return;
      }
      try {
        const http = initializeHttpProvider();
        const response = await http.mlParcel.deletePlugin({ clientId, pluginId: mlParcelPluginId });
        if (response.success) {
          updatePluginStatus(id, 'disconnected');
          toast.success('Plugin desconectado exitosamente');
          await fetchUserPlugins();
        } else {
          toast.error('Error al desconectar plugin');
        }
      } catch (error) {
        console.error('Error disconnecting plugin:', error);
        toast.error('Error al desconectar plugin');
      }
    } else {
      updatePluginStatus(id, 'disconnected');
    }
  };

  const fetchUserPlugins = useCallback(async () => {
    const clientId = profile?.credentials.clientId;
    if (!clientId) return;

    setLoading(true);
    try {
      const http = initializeHttpProvider();
      const response = await http.envioFlex.getUserPlugins({ clientId });

      if ('data' in response && response.success && Array.isArray(response.data)) {
        const userPlugins = (response as IResUserPlugins).data;

        const mercadoFlexPlugin = userPlugins.find(
          (p) =>
            p.platform.toLowerCase().includes('mercado') &&
            !p.platform.toLowerCase().includes('fulfillment') &&
            !p.platform.toLowerCase().includes('parcel'),
        );
        if (mercadoFlexPlugin) {
          updatePluginStatus(
            'mercado-envios',
            mercadoFlexPlugin.active ? 'connected' : 'disconnected',
          );
          setMercadoPluginId(mercadoFlexPlugin.id);
          dispatch(setEnvioFlexAccess(mercadoFlexPlugin.active));
        } else {
          updatePluginStatus('mercado-envios', 'disconnected');
          dispatch(setEnvioFlexAccess(false));
        }

        const fulfillPlugin = userPlugins.find((p) =>
          p.platform.toLowerCase().includes('fulfillment'),
        );
        if (fulfillPlugin) {
          updatePluginStatus(
            'mercado-envios-fulfill99',
            fulfillPlugin.active ? 'connected' : 'disconnected',
          );
          setMlFulfillPluginId(fulfillPlugin.id);
          dispatch(setMLFulfillAccess(fulfillPlugin.active));
        } else {
          updatePluginStatus('mercado-envios-fulfill99', 'disconnected');
          dispatch(setMLFulfillAccess(false));
        }

        const parcelPlugin = userPlugins.find((p) => p.platform.toLowerCase().includes('parcel'));
        if (parcelPlugin) {
          updatePluginStatus('mercado-parcel', parcelPlugin.active ? 'connected' : 'disconnected');
          setMlParcelPluginId(parcelPlugin.id);
          dispatch(setMLParcelAccess(parcelPlugin.active));
        } else {
          updatePluginStatus('mercado-parcel', 'disconnected');
          dispatch(setMLParcelAccess(false));
        }

        const activeCount = [mercadoFlexPlugin, fulfillPlugin, parcelPlugin].filter(
          (p) => p?.active,
        ).length;
        setActiveMlCount(activeCount);

        const ownerPlatform =
          [mercadoFlexPlugin, fulfillPlugin, parcelPlugin].find((p) => p?.webhookPlatform)
            ?.webhookPlatform ?? null;
        setWebhookPlatformOwner(ownerPlatform);

        // FALABELLA (obtenido del listado unificado de plugins del usuario)
        if (isFalabellaEnabledCountry(profile?.country)) {
          const falabellaPlugin = userPlugins.find(
            (plugin) => plugin.platform?.toLowerCase() === 'falabella',
          );
          if (falabellaPlugin) {
            updatePluginStatus('falabella', falabellaPlugin.active ? 'connected' : 'disconnected');
            setFalabellaPluginId(falabellaPlugin.id);
            dispatch(setFalabellaAccess(falabellaPlugin.active));
          } else {
            updatePluginStatus('falabella', 'disconnected');
            dispatch(setFalabellaAccess(false));
          }
        } else {
          updatePluginStatus('falabella', 'disabled');
          dispatch(setFalabellaAccess(false));
        }
      }
    } catch (error) {
      console.error('Error fetching user plugins:', error);
      toast.error('Error al cargar plugins');
    } finally {
      setLoading(false);
    }
  }, [profile?.credentials.clientId, profile?.country, dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const traceId = params.get('traceId');

    if (success === 'true') {
      toast.success('Plugin conectado exitosamente');
      window.history.replaceState({}, '', '/my-plugins');
    } else if (success === 'false') {
      const errorMessage = traceId
        ? `Error al conectar el plugin. Trace ID: ${traceId}`
        : 'Error al conectar el plugin';
      toast.error(errorMessage);
      window.history.replaceState({}, '', '/my-plugins');
    }
  }, []);

  useEffect(() => {
    fetchUserPlugins();
  }, [profile?.credentials.clientId, fetchUserPlugins]);

  const clientId = profile?.credentials.clientId || '';

  return {
    configurablePlugins,
    upcomingPlugins,
    documentationPlugins,
    clientId,
    handleConnect,
    handleDisconnect,
    handleActiveConnector,
    handleActiveConfiguration,
    activeConfiguration,
    activeConnector,
    mercadoPluginId,
    mlFulfillPluginId,
    mlParcelPluginId,
    falabellaPluginId,
    webhookPlatformOwner,
    activeMlCount,
    loading,
    fetchUserPlugins,
  };
};

export default useMyPlugins;
